import { FC, useMemo } from 'react';

import * as yup from 'yup';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { ServerErrors } from 'components';
import { mapServerError } from 'core';
import { useActivateAccountAndSetPasswordMutation, useActivateAccountMigrateMutation } from 'store/signup/signupAPI';

import { Button } from '@britishcouncil/react-solas';
import { MainContainer, NewPasswordField, PageContainer, PasswordField } from '@britishcouncil/react-solas-ors3';
import { estimatePasswordCompliance, useFormFields, useServerErrors } from '@britishcouncil/react-forms';

import { ActivationFormModel } from '../types';

import { ActivationSuccess } from '.';

const VALIDATION_SCHEMA = yup.object().shape({
  password: yup
    .string()
    .required('Password is required.')
    .test('password', 'Password is to weak.', (pass) => estimatePasswordCompliance(pass).isCompliant),
  confirmPassword: yup
    .string()
    .required('Please confirm password.')
    .oneOf([yup.ref('password')], "Seems that you've made some typo in this field."),
});

export const ActivationForm: FC<{
  token: string;
  email: string;
  isMigration: true;
  candidateId: number;
} | {
  token: string;
  email: string;
  isMigration: false;
}> = (params) => {
  const [activate, activateData] = useActivateAccountAndSetPasswordMutation();
  const [activateMinor, activateMinorData] = useActivateAccountMigrateMutation();

  const formik = useFormik<ActivationFormModel>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (formValues) => {
      if(params.isMigration){
        activateMinor({
          ...formValues,
          ...params,
          candidateId: params.candidateId
        });
      } else {
        activate({
          ...formValues,
          ...params,
        });
      }
    },
  });

  const fields = useFormFields(formik);
  const serverErrors = useMemo(
    () => {
      let activateDataErrors = (activateData.isError ? mapServerError(activateData.error) : []);
      let activateMinorDataErrors = (activateMinorData.isError ? mapServerError(activateMinorData.error) : []);
      return activateDataErrors.concat(activateMinorDataErrors);
    },
    [activateData.error, activateData.isError, activateMinorData.error, activateMinorData.isError]
  );
  const mainErrors = useServerErrors(formik, serverErrors);

  if (activateData.isSuccess || activateMinorData.isSuccess)
    return (
      <MainContainer>
        <ActivationSuccess />
      </MainContainer>
    );

  return (
    <PageContainer className="page-container">
      <form>
        <div className="form-group">
          <label>Email: </label>
          <EmailValue>{params.email}</EmailValue>
        </div>
        <NewPasswordField label="Password" {...fields.password} />
        <PasswordField label="Confirm password" {...fields.confirmPassword} />
        {!activateData.isLoading && <ServerErrors errors={mainErrors} />}
        <Button
          onClick={formik.submitForm}
          type="button"
          disabled={activateData.isLoading}
          loading={activateData.isLoading}
        >
          Activate
        </Button>
      </form>
    </PageContainer>
  );
};

const EmailValue = styled.strong`
  margin-left: 10px;
`;
